import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ClaimPipeline } from '@/api/generated/client/models/ClaimPipeline';
import type { CreateClaimPipelineRequest } from '@/api/generated/client/models/CreateClaimPipelineRequest';
import type { UpdateClaimPipelineRequest } from '@/api/generated/client/models/UpdateClaimPipelineRequest';
import { getApiErrorMessage } from '@/lib/errors';
import { useToast } from '@/hooks/use-toast';

export function useClaimPipelines(options: { includeInactive?: boolean } = {}) {
    const includeInactive = options.includeInactive ?? false;
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const query = useQuery<ClaimPipeline[]>({
        queryKey: ['claim-pipelines', includeInactive],
        queryFn: async () => {
            const response = await apiClient.claimPipelines.getClaimPipelines({
                includeInactive,
            });
            return response.pipelines ?? [];
        },
    });

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['claim-pipelines'] });
    const createMutation = useMutation({
        mutationFn: (data: CreateClaimPipelineRequest) => apiClient.claimPipelines.createClaimPipeline({ createClaimPipelineRequest: data }),
        onSuccess: invalidate,
        onError: async (error: unknown) => {
            toast({ title: 'Cannot create workflow', description: await getApiErrorMessage(error), variant: 'destructive' });
        },
    });
    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateClaimPipelineRequest }) => apiClient.claimPipelines.updateClaimPipeline({ id, updateClaimPipelineRequest: data }),
        onSuccess: invalidate,
        onError: async (error: unknown) => {
            toast({ title: 'Cannot update workflow', description: await getApiErrorMessage(error), variant: 'destructive' });
        },
    });
    const deleteMutation = useMutation({
        mutationFn: (id: number) => apiClient.claimPipelines.deleteClaimPipeline({ id }),
        onSuccess: invalidate,
        onError: async (error: unknown) => {
            toast({ title: 'Cannot delete workflow', description: await getApiErrorMessage(error), variant: 'destructive' });
        },
    });

    return {
        pipelines: query.data ?? [],
        isLoading: query.isLoading,
        error: query.error,
        createPipeline: createMutation.mutateAsync,
        updatePipeline: updateMutation.mutateAsync,
        deletePipeline: deleteMutation.mutateAsync,
        isMutating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    };
}